import { Button } from '@chakra-ui/react';
import { toast } from 'react-toastify';

import { useTaughtCallUrlLazyQuery } from '../../gql/generated/graphql';

type ViewCallLinkProps = {
  noteId: number;
};

const ViewCallLink = ({ noteId }: ViewCallLinkProps) => {
  const [fetchTaughtCallUrl, { loading }] = useTaughtCallUrlLazyQuery({
    fetchPolicy: 'network-only',
  });

  const handleViewCall = async () => {
    // Open synchronously inside the click so the browser treats it as user-initiated.
    // No 'noopener' — per spec it forces window.open to return null, which would block
    // navigating the tab after the async lookup. We clear opener manually before assigning.
    const newTab = window.open('', '_blank');
    if (!newTab) {
      toast.error('Browser blocked opening the Taught link.');
      return;
    }

    // Codegen types note id as number, but GraphQL ID serializes as a string on the wire.
    // Coerce so it satisfies the taughtCallUrl($noteId: Int) variable.
    const id = Number(noteId);
    if (!Number.isFinite(id)) {
      newTab.close();
      toast.error('Unable to open call in Taught.');
      return;
    }

    try {
      const { data } = await fetchTaughtCallUrl({ variables: { noteId: id } });
      const url = data?.taughtCallUrl;
      if (!url) {
        newTab.close();
        toast.error("This call isn't available in Taught yet.");
        return;
      }
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
        newTab.close();
        toast.error('Unable to open call in Taught.');
        return;
      }
      newTab.opener = null;
      newTab.location.href = url;
    } catch {
      newTab.close();
      toast.error('Unable to open call in Taught.');
    }
  };

  return (
    <Button
      aria-label="View call recording in Taught"
      color="blue.600"
      fontSize="sm"
      isLoading={loading}
      mt={1}
      onClick={handleViewCall}
      size="sm"
      textDecoration="underline"
      variant="link"
    >
      View Call
    </Button>
  );
};

export default ViewCallLink;
